import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { listOrders } from '@/api/orders'
import { OrderTable } from '@/components/orders/OrderTable'
import {
  OrderFilters,
  type OrderFilterState,
} from '@/components/orders/OrderFilters'
import { OrderDrawer } from '@/components/orders/OrderDrawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import { KanbanCard } from '@/components/orders/KanbanCard'
import { PageHeader } from '@/components/layout/PageHeader'
import { useRoleOrderParams } from '@/hooks/useRoleOrderParams'
import { Button } from '@/components/ui/Button'
import { Loader } from '@/components/ui/Loader'
import { useAuthStore } from '@/store/authStore'

const defaultFilters: OrderFilterState = {
  status: [],
  search: '',
  dateFrom: '',
  dateTo: '',
  customerKind: '',
}

export function OrdersPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const nav = useNavigate()
  const mobile = useIsMobile()
  const profile = useAuthStore((s) => s.profile)
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState<OrderFilterState>(defaultFilters)
  const [applied, setApplied] = useState<OrderFilterState>(defaultFilters)

  // Авто-применение поля «Поиск» с debounce 300мс. Остальные фильтры
  // (статус, даты) по-прежнему применяются по кнопке «Применить» — там
  // важна предсказуемость, лишний rerender не нужен.
  useEffect(() => {
    if (draft.search === applied.search) return
    const t = setTimeout(() => {
      setApplied((prev) => ({ ...prev, search: draft.search }))
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [draft.search, applied.search])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const roleParams = useRoleOrderParams()

  const canCreateOrder =
    profile?.role === 'manager' ||
    profile?.role === 'admin' ||
    profile?.role === 'dispatcher' ||
    profile?.role === 'customer_supervisor'

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['orders', 'list', page, applied, roleParams],
    queryFn: () =>
      listOrders({
        page,
        pageSize: 25,
        status: applied.status.length ? applied.status : undefined,
        search: applied.search || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        customerKind: applied.customerKind || undefined,
        ...roleParams,
      }),
    placeholderData: keepPreviousData,
  })

  // Баннер «Загрузка… Nс» показываем только когда на экране устаревшие
  // данные (смена фильтра/страницы → keepPreviousData отдаёт прошлый
  // ответ, isPlaceholderData=true). Фоновые refetch актуальных данных
  // (window-focus, realtime-инвалидации, invalidate после сохранения)
  // не должны рисовать секундомер поверх свежей таблицы.
  const tableLoading = isFetching && (isPlaceholderData || !data)

  const orders = data?.items ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / 25))

  return (
    <div>
      <OrderDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false)
          void qc.invalidateQueries({ queryKey: ['orders'] })
        }}
      />
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.inSelectionSubtitle', { n: orders.length, total })}
        actions={
          canCreateOrder ? (
            <Button variant="primary" onClick={() => setDrawerOpen(true)}>
              {t('orders.newOrder')}
            </Button>
          ) : undefined
        }
      />
      <div className="mb-4">
        <OrderFilters
          value={draft}
          onChange={setDraft}
          onApply={() => {
            setApplied(draft)
            setPage(1)
            void qc.invalidateQueries({ queryKey: ['orders', 'list'] })
          }}
          onReset={() => {
            setDraft(defaultFilters)
            setApplied(defaultFilters)
            setPage(1)
          }}
        />
      </div>
      {isLoading && !data ? (
        <Loader centered className="py-8" />
      ) : mobile ? (
        <div className="mt-4 space-y-3">
          {orders.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--stryzh-color-text-secondary)]">
              {t('common.noData')}
            </p>
          ) : (
            orders.map((o) => <KanbanCard key={o.id} order={o} />)
          )}
        </div>
      ) : (
        <div className="mt-4">
          <OrderTable
            orders={orders}
            loading={tableLoading}
            onRowClick={(id) => nav(`/orders/${id}`)}
          />
        </div>
      )}
      {!mobile && pages > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="rounded border border-[var(--stryzh-color-border-default)] px-3 py-1 text-sm disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('orders.prev')}
          </button>
          <span className="text-sm text-[var(--stryzh-color-text-secondary)]">
            {page} / {pages}
          </span>
          <button
            type="button"
            className="rounded border border-[var(--stryzh-color-border-default)] px-3 py-1 text-sm disabled:opacity-40"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
          >
            {t('orders.next')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
